import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Track, generateTrackPersona, extractTrackPersonaIdentifiers, ensureTrackPersonaIdentifiers } from "../../data/schema";
import { showToast } from "components/ui-kit/Toast";
import CameraSVG from "assets/camera.svg";

type Props = {
  open: boolean;
  track: Track | null;
  onClose: () => void;
  onSuccess?: () => void;
  origin?: "ai-compose" | "library";
  onPersonaCreated?: () => void;
};

const NAME_LIMIT = 50;
const DESCRIPTION_LIMIT = 200;

export const CreatePersonaModal: React.FC<Props> = ({
  open,
  track,
  onClose,
  onSuccess,
  origin = "ai-compose",
  onPersonaCreated,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [identifiers, setIdentifiers] = useState<{ taskId?: string; audioId?: string }>({});
  const [isLoadingIdentifiers, setIsLoadingIdentifiers] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Stores the raw file for future backend integration.
  const avatarFileRef = useRef<File | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setError(null);
      setIsSubmitting(false);
      setIdentifiers({});
      avatarFileRef.current = null;
      setAvatarPreview(null);
      if (track) {
        console.debug("[CreatePersonaModal] open modal snapshot", {
          trackId: track.id,
          params: track.params,
        });
      }
    }
  }, [open, track]);

  const hydrateIdentifiers = useCallback(async (): Promise<{ taskId?: string; audioId?: string }> => {
    if (!track) return {};
    setIsLoadingIdentifiers(true);
    try {
      const result = await ensureTrackPersonaIdentifiers(track.id);
      setIdentifiers(result);
      return result;
    } catch (err) {
      console.error("Failed to ensure persona identifiers", err);
      const fallback = track ? extractTrackPersonaIdentifiers(track) : {};
      setIdentifiers(fallback);
      return fallback;
    } finally {
      setIsLoadingIdentifiers(false);
    }
  }, [track]);

  useEffect(() => {
    if (!open || !track) {
      setIdentifiers({});
      return;
    }
    const current = extractTrackPersonaIdentifiers(track);
    setIdentifiers(current);
    if (!current.audioId && current.taskId) {
      hydrateIdentifiers();
    }
  }, [open, track, hydrateIdentifiers]);

  const isReady = Boolean(identifiers.taskId && identifiers.audioId);

  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!track || isSubmitting) return;

    if (!isReady) {
      await hydrateIdentifiers();
    }
    const latestResult =
      identifiers.taskId && identifiers.audioId ? identifiers : await hydrateIdentifiers();
    console.debug("[CreatePersonaModal] identifiers snapshot", {
      latestResult,
      trackId: track.id,
      trackParams: track.params,
    });
    const latestIdentifiers =
      latestResult.taskId && latestResult.audioId ? latestResult : extractTrackPersonaIdentifiers(track);
      console.log(latestIdentifiers, "latestIdentifiers");
    if (!latestIdentifiers.audioId || !latestIdentifiers.taskId) {
      console.warn("[CreatePersonaModal] persona identifiers missing audio/task", {
        trackId: track.id,
        params: track.params,
      });
      setError("Track is still processing. Try again once the audio is ready.");
      return;
    }

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    if (!trimmedName) {
      setError("Name is required");
      return;
    }
    if (!trimmedDescription) {
      setError("Description is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert avatar file to base64 if present
      let imageBase64: string | null = null;
      if (avatarFileRef.current) {
        try {
          imageBase64 = await fileToBase64(avatarFileRef.current);
          console.log('[CreatePersonaModal] Avatar converted to base64', {
            fileName: avatarFileRef.current.name,
            size: avatarFileRef.current.size,
            type: avatarFileRef.current.type,
          });
        } catch (err) {
          console.error('[CreatePersonaModal] Failed to convert avatar to base64', err);
          // Continue without image if conversion fails
        }
      }

      await generateTrackPersona(track.id, trimmedName, trimmedDescription, imageBase64);
      const successMessage =
        origin === "library"
          ? "Persona created. Open AI Compose → Custom to use it."
          : "Persona created. Select it from your personas list.";
      showToast(successMessage, undefined, "success");
      onSuccess?.();
      onClose();
      if (origin === "ai-compose") {
        requestAnimationFrame(() => {
          onPersonaCreated?.();
        });
      }
    } catch (err: any) {
      console.error("Failed to generate persona", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to generate persona. Please try again.";
      setError(message);
      showToast(message, undefined, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open || !track) {
    return null;
  }

  const content = (
    <Overlay onClick={handleClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Create Persona</Title>
          <Subtitle>{track.title || "Untitled track"}</Subtitle>
        </Header>

        {!isReady && (
          <InfoBanner>
            {isLoadingIdentifiers
              ? "Fetching track details. Please wait..."
              : "Persona generation will be available once the track finishes processing."}
          </InfoBanner>
        )}

        <FieldGroup>
          <NameRow>
            <AvatarWrapper>
              <AvatarCircle
                type="button"
                $hasImage={Boolean(avatarPreview)}
                onClick={() => !isSubmitting && fileInputRef.current?.click()}
                disabled={isSubmitting}
              >
                {avatarPreview ? (
                  <AvatarImage src={avatarPreview} alt="Persona avatar preview" />
                ) : (
                  <AvatarPlaceholder>
                    <CameraSVG />
                  </AvatarPlaceholder>
                )}
              </AvatarCircle>
              
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  avatarFileRef.current = file ?? null;
                  if (file) {
                    const objectUrl = URL.createObjectURL(file);
                    setAvatarPreview(objectUrl);
                  } else {
                    setAvatarPreview(null);
                  }
                }}
              />
            </AvatarWrapper>
            <NameInputColumn>
              <NameInput
                id="persona-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value.slice(0, NAME_LIMIT));
                  setError(null);
                }}
                maxLength={NAME_LIMIT}
                placeholder="Enter persona name"
                disabled={isSubmitting}
              />
              <CharacterHint>{name.length}/{NAME_LIMIT}</CharacterHint>
            </NameInputColumn>
          </NameRow>
        </FieldGroup>

        <FieldGroup>
          <DescriptionTextarea
            id="persona-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT));
              setError(null);
            }}
            maxLength={DESCRIPTION_LIMIT}
            placeholder="Describe the persona you'd like to create"
            disabled={isSubmitting}
          />
          <CharacterHint>{description.length}/{DESCRIPTION_LIMIT}</CharacterHint>
        </FieldGroup>

        {error && <ErrorText>{error}</ErrorText>}

        <Actions>
          <SecondaryButton onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingIdentifiers}
          >
            {isSubmitting ? "Generating..." : "Generate"}
          </PrimaryButton>
        </Actions>
      </Dialog>
    </Overlay>
  );

  if (typeof document === "undefined") {
    return content;
  }
  return createPortal(content, document.body);
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11000;
  padding: 20px;
`;

const Dialog = styled.div`
  width: 480px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e9ecef;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.16);
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #111;
`;

const Subtitle = styled.span`
  font-size: 14px;
  color: #6c757d;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const NameInputColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const AvatarCircle = styled.button<{ $hasImage?: boolean }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: ${p => (p.$hasImage ? "2px solid transparent" : "2px dashed #ced4da")};
  background: ${p => (p.$hasImage ? "#f8f9fa" : "#f1f3f5")};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #6777ff;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6777ff;
  svg {
    width: 22px;
    height: 22px;
  }
`;

const CameraButton = styled.button`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: linear-gradient(135deg, #7987ff 0%, #ff7b7d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: #fff;
  }
`;

const CameraIcon = styled(CameraSVG)`
  width: 16px;
  height: 16px;
  stroke: #fff;
`;

const AvatarHint = styled.span`
  font-size: 11px;
  color: #6c757d;
  text-align: center;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #333;
`;

const NameInput = styled.input`
  height: 42px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #ced4da;
  font-size: 14px;
  color: #111;
  outline: none;

  &:focus {
    border-color: #6777ff;
    box-shadow: 0 0 0 3px rgba(103, 119, 255, 0.15);
  }

  &:disabled {
    background: #f1f3f5;
    cursor: not-allowed;
  }
`;

const DescriptionTextarea = styled.textarea`
  min-height: 120px;
  resize: vertical;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid #ced4da;
  font-size: 14px;
  color: #111;
  outline: none;

  &:focus {
    border-color: #6777ff;
    box-shadow: 0 0 0 3px rgba(103, 119, 255, 0.15);
  }

  &:disabled {
    background: #f1f3f5;
    cursor: not-allowed;
  }
`;

const CharacterHint = styled.span`
  align-self: flex-end;
  font-size: 12px;
  color: #9aa0a6;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;

const SecondaryButton = styled.button<{ disabled?: boolean }>`
  min-width: 100px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #dee2e6;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  opacity: ${p => (p.disabled ? 0.6 : 1)};
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #cbd3da;
  }
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  min-width: 140px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(90deg, #7987ff 0%, #ff7b7d 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  opacity: ${p => (p.disabled ? 0.6 : 1)};
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #d93025;
`;

const InfoBanner = styled.div`
  background: #f8f9ff;
  border: 1px solid #dee2ff;
  color: #495057;
  padding: 12px;
  border-radius: 10px;
  font-size: 13px;
`;

